import React, { useCallback, useMemo, useState } from 'react';

import { Popover, Typography } from '@mui/material';
import { AlineAvatar } from 'src/components';
import { FilterPopoverContent } from 'src/components/workflow/workflow-list/filter-contents/FilterPopoverContent';
import { Apollo } from 'src/gql/generated';
import { useGetOrgMembers } from 'src/hooks';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  optionContent: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
  },
  popoverContent: {
    maxHeight: 300,
    minWidth: 250,
    overflowY: 'auto',
    padding: theme.spacing(1),
  },
  popoverPaper: {
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    minWidth: 300,
    padding: theme.spacing(2, 0),
  },
}));

export type AssigneeOption = {
  avatarColor: string | null;
  displayName: string;
  email: string;
  firstName: string | null;
  id: string;
};

interface AssigneePickerPopoverProps {
  onAssigneesChange: (assigneeIds: string[]) => void;
  renderTrigger: (selectedAssignees: AssigneeOption[], isOpen?: boolean) => React.ReactNode;
  selectedAssigneeIds: string[];
}

export function AssigneePickerPopover({
  onAssigneesChange,
  renderTrigger,
  selectedAssigneeIds,
}: AssigneePickerPopoverProps): JSX.Element {
  const { classes } = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const { orgMembers } = useGetOrgMembers({
    variables: { filter: Apollo.OrgMemberFilter.Active },
  });

  const options = useMemo(
    () =>
      orgMembers.map(member => ({
        ...member,
        label: member.displayName,
      })),
    [orgMembers]
  );

  const selectedAssignees = useMemo(
    () => options.filter(opt => selectedAssigneeIds.includes(opt.id)),
    [options, selectedAssigneeIds]
  );

  const selectedIds = useMemo(() => new Set(selectedAssigneeIds), [selectedAssigneeIds]);

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchTerm('');
  }, []);

  const handleToggle = useCallback(
    (memberId: string) => {
      const isSelected = selectedAssigneeIds.includes(memberId);
      const newIds = isSelected
        ? selectedAssigneeIds.filter(id => id !== memberId)
        : [...selectedAssigneeIds, memberId];
      onAssigneesChange(newIds);
    },
    [selectedAssigneeIds, onAssigneesChange]
  );

  const renderOption = useCallback(
    (option: (typeof options)[number], _isSelected: boolean) => (
      <div className={classes.optionContent}>
        <AlineAvatar avatarInfo={option} styles={{ size: 'xs' }} />
        <Typography variant="body2">{option.displayName}</Typography>
      </div>
    ),
    [classes.optionContent]
  );

  return (
    <>
      <div ref={setAnchorEl} style={{ cursor: 'pointer' }} onClick={handleClick}>
        {renderTrigger(selectedAssignees, open)}
      </div>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          horizontal: 'left',
          vertical: 'bottom',
        }}
        open={open}
        transformOrigin={{
          horizontal: 'left',
          vertical: 'top',
        }}
        onClose={handleClose}
      >
        <div className={classes.popoverContent}>
          <FilterPopoverContent
            showSearch
            options={options}
            renderOption={renderOption}
            searchPlaceholder="Search assignees..."
            searchTerm={searchTerm}
            selectedIds={selectedIds}
            onSearchChange={setSearchTerm}
            onToggle={handleToggle}
          />
        </div>
      </Popover>
    </>
  );
}
